import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Chat } from 'app/layout/common/quick-chat/quick-chat.types';
import {
    BehaviorSubject,
    map,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class QuickChatService {
    private _chat: BehaviorSubject<Chat> = new BehaviorSubject(null);
    private _chats: BehaviorSubject<Chat[]> = new BehaviorSubject<Chat[]>(null);

    // Static chat data for development
    private _staticChats: Chat[] = [
        {
            id: '1',
            contactId: 'quantum-ai',
            contact: {
                id: 'quantum-ai',
                name: 'Quantum AI Assistant',
                avatar: 'assets/images/avatars/brian-hughes.jpg'
            },
            unreadCount: 2,
            muted: false,
            lastMessage: 'Your quantum circuit optimization is complete. Would you like to review the results?',
            lastMessageAt: new Date().toISOString(),
            messages: [
                {
                    id: 'msg1',
                    chatId: '1',
                    contactId: 'quantum-ai',
                    isMine: false,
                    value: 'Hello! I\'m your Quantum AI Assistant. How can I help you today?',
                    createdAt: new Date(Date.now() - 3600000).toISOString()
                },
                {
                    id: 'msg2',
                    chatId: '1',
                    contactId: 'dev-user',
                    isMine: true,
                    value: 'Can you help me optimize my quantum circuit?',
                    createdAt: new Date(Date.now() - 3000000).toISOString()
                },
                {
                    id: 'msg3',
                    chatId: '1',
                    contactId: 'quantum-ai',
                    isMine: false,
                    value: 'Your quantum circuit optimization is complete. Would you like to review the results?',
                    createdAt: new Date().toISOString()
                }
            ]
        },
        {
            id: '2',
            contactId: 'ml-assistant',
            contact: {
                id: 'ml-assistant',
                name: 'ML Model Trainer',
                avatar: 'assets/images/avatars/female-18.jpg'
            },
            unreadCount: 0,
            muted: false,
            lastMessage: 'Training completed with 94.2% accuracy.',
            lastMessageAt: new Date(Date.now() - 1800000).toISOString(),
            messages: [
                {
                    id: 'msg4',
                    chatId: '2',
                    contactId: 'ml-assistant',
                    isMine: false,
                    value: 'Starting quantum machine learning model training...',
                    createdAt: new Date(Date.now() - 7200000).toISOString()
                },
                {
                    id: 'msg5',
                    chatId: '2',
                    contactId: 'ml-assistant',
                    isMine: false,
                    value: 'Training completed with 94.2% accuracy.',
                    createdAt: new Date(Date.now() - 1800000).toISOString()
                }
            ]
        },
        {
            id: '3',
            contactId: 'error-mitigation',
            contact: {
                id: 'error-mitigation',
                name: 'Error Mitigation System',
                avatar: 'assets/images/avatars/male-16.jpg'
            },
            unreadCount: 1,
            muted: false,
            lastMessage: 'Detected quantum decoherence. Applying error correction protocols.',
            lastMessageAt: new Date(Date.now() - 900000).toISOString(),
            messages: [
                {
                    id: 'msg6',
                    chatId: '3',
                    contactId: 'error-mitigation',
                    isMine: false,
                    value: 'Monitoring quantum state fidelity...',
                    createdAt: new Date(Date.now() - 1800000).toISOString()
                },
                {
                    id: 'msg7',
                    chatId: '3',
                    contactId: 'error-mitigation',
                    isMine: false,
                    value: 'Detected quantum decoherence. Applying error correction protocols.',
                    createdAt: new Date(Date.now() - 900000).toISOString()
                }
            ]
        }
    ];

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for chat
     */
    get chat$(): Observable<Chat> {
        return this._chat.asObservable();
    }

    /**
     * Getter for chat
     */
    get chats$(): Observable<Chat[]> {
        return this._chats.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get chats
     */
    getChats(): Observable<any> {
        // Return static chats for development
        return of([...this._staticChats]).pipe(
            tap((response: Chat[]) => {
                this._chats.next(response);
            })
        );

        // Original API call commented out for development
        /*
        return this._httpClient.get<Chat[]>('api/apps/chat/chats').pipe(
            tap((response: Chat[]) => {
                this._chats.next(response);
            })
        );
        */
    }

    /**
     * Get chat
     *
     * @param id
     */
    getChatById(id: string): Observable<any> {
        // For development: find chat from static data
        const foundChat = this._staticChats.find(chat => chat.id === id);
        
        if (!foundChat) {
            return throwError('Could not found chat with id of ' + id + '!');
        }

        return of(foundChat).pipe(
            map((chat) => {
                // Update the chat
                this._chat.next(chat);
                // Return the chat
                return chat;
            })
        );

        // Original API call commented out for development
        /*
        return this._httpClient
            .get<Chat>('api/apps/chat/chat', { params: { id } })
            .pipe(
                map((chat) => {
                    this._chat.next(chat);
                    return chat;
                }),
                switchMap((chat) => {
                    if (!chat) {
                        return throwError(
                            'Could not found chat with id of ' + id + '!'
                        );
                    }
                    return of(chat);
                })
            );
        */
    }
}